"use client";
// Build canonical Cloudflare R2 URL for a media reference.
// Accepts objects with `publicId`/`url`/`fileName`/`name`, or string IDs/URLs.
// If the reference already contains a `shared_media/...` path (publicId or URL),
// preserve the subpath (folders). Otherwise fall back to basename.
import { getMediaUrl } from "@/lib/utils";

// Resolve media to usable URL string
function resolveMedia(media, fallbackKey) {
  if (!media) return fallbackKey ? getMediaUrl(fallbackKey) : "";
  if (typeof media === "string") return media;
  if (typeof media === "object") {
    if (media.url) return media.url;
    if (media.path) return media.path;
    const candidate = media.publicId || media.public_id || media.id || media.fileName || media.name;
    if (candidate) return getMediaUrl(candidate);
  }
  return fallbackKey ? getMediaUrl(fallbackKey) : "";
}
import Footer from "@/components/ar-IQ/Footer";
import Navbar from "@/components/ar-IQ/Navbar";
import { CardBody, CardContainer, CardItem } from "@/components/ui/3d-card";
import { useLanguage } from "@/contexts/LanguageContext";
import { getPortfolio } from "@/lib/api";
import PortfolioModel from "@/models/portfolio";
import {
  motion,
  useInView,
  useMotionValue,
  useScroll,
  useSpring,
  useTransform,
} from "framer-motion";
import Image from "next/image";
import Link from "next/link";
import React, { useEffect, useRef, useState } from "react";

const PortfolioPage = () => {
  const filterRef = useRef(null);
  const sectionRef = useRef(null);
  const heroRef = useRef(null);
  const videoRef = useRef(null);

  const [floatingShapes, setFloatingShapes] = useState([]);
  const [projects, setProjects] = useState([]);
  const [activeFilter, setActiveFilter] = useState("all");
  const [mounted, setMounted] = useState(false);
  const [heroData, setHeroData] = useState({
    title: "معرض أعمالنا",
    titleHighlight: "",
    subtitle: "اكتشف مشاريعنا المميزة في عالم الإعلان والتسويق الرقمي",
    video: null,
  });
  const [projectsData, setProjectsData] = useState({
    title: "مشاريعنا",
    subtitle: "نقدم حلول إعلانية مبتكرة ومؤثرة",
    images: [],
  });
  const { lang } = useLanguage();

  // Scroll animations
  const [scrollYProgress, setScrollYProgress] = useState(null);
  const [filterProgress, setFilterProgress] = useState(null);

  const heroY = scrollYProgress
    ? useTransform(scrollYProgress, [0, 1], ["0%", "30%"])
    : null;
  const heroOpacity = scrollYProgress
    ? useTransform(scrollYProgress, [0, 0.6], [1, 0.2])
    : null;

  const filterOpacity = filterProgress
    ? useTransform(filterProgress, [0, 0.5, 1], [0, 0.5, 1])
    : null;
  const filterScale = filterProgress
    ? useTransform(filterProgress, [0, 1], [0.98, 1.02])
    : null;

  // Mouse parallax for hero content
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);
  const parallaxX = useSpring(mouseX, {
    stiffness: 120,
    damping: 15,
    mass: 0.2,
  });
  const parallaxY = useSpring(mouseY, {
    stiffness: 120,
    damping: 15,
    mass: 0.2,
  });

  const isHeroInView = useInView(heroRef, { once: true });

  useEffect(() => {
    setMounted(true);
    const shapes = Array.from({ length: 15 }, (_, i) => ({
      id: i,
      top: 10 + Math.random() * 80,
      left: 10 + Math.random() * 80,
      delay: Math.random() * 2,
      duration: 4 + Math.random() * 2,
    }));
    setFloatingShapes(shapes);

    // Fetch portfolio data
    const fetchPortfolioData = async () => {
      try {
        const data = await getPortfolio("ar");
        if (data) {
          console.log(data.hero.title);
          if (data.hero) {
            console.log(data.hero.title);
            setHeroData({
              title: data.hero.title || "معرض أعمالنا",
              titleHighlight: data.hero.titleHighlight || "",
              subtitle:
                data.hero.subtitle ||
                "اكتشف مشاريعنا المميزة في عالم الإعلان والتسويق الرقمي",
              video: data.hero.video,
            });
          }
          // Load projects from API, fallback to static data
          if (
            data.projects &&
            Array.isArray(data.projects) &&
            data.projects.length > 0
          ) {
            // Convert API projects to match static data structure
            const apiProjects = data.projects.map((project) => {
              // Helper function to extract string value from localized or plain text
              const extractText = (value) => {
                if (!value) return "";
                if (typeof value === "string") return value;
                if (typeof value === "object" && !Array.isArray(value)) {
                  // Return the value for current language, fallback to other language
                  return value.ar || value.en || "";
                }
                return String(value);
              };

              return {
                id: project.id,
                slug: project.slug,
                title: extractText(project.title),
                description: extractText(project.description),
                fullDescription: extractText(project.fullDescription),
                category: project.category || "",
                categoryName: extractText(project.categoryName),
                client: extractText(project.client),
                year: project.year || "",
                duration: extractText(project.duration),
                thumbnail: project.thumbnail?.url || (project.thumbnail ? getMediaUrl(project.thumbnail) : ""),
                heroImage: project.heroImage?.url || (project.heroImage ? getMediaUrl(project.heroImage) : ""),
                detailImage:
                  project.detailImage?.url || (project.detailImage ? getMediaUrl(project.detailImage) : ""),
                gallery: (project.gallery || []).map(
                  (img) => (img?.url ? img.url : (img ? getMediaUrl(img) : ""))
                ),
                services: (project.services || []).map((service) => extractText(service)),
                featured: project.featured || false,
              };
            });
            setProjects(apiProjects);
          } else {
            // Fallback to static data - normalize static projects to canonical R2 URLs
            const staticProjects = PortfolioModel.getAllProjects().map((p) => ({
              ...p,
              thumbnail: p.thumbnail ? getMediaUrl(p.thumbnail) : "",
              heroImage: p.heroImage ? getMediaUrl(p.heroImage) : "",
              detailImage: p.detailImage ? getMediaUrl(p.detailImage) : "",
              gallery: (p.gallery || []).map((g) => (g ? getMediaUrl(g) : "")),
            }));
            setProjects(staticProjects);
          }
        } else {
          // Fallback to static data - normalize
          const staticProjects = PortfolioModel.getAllProjects().map((p) => ({
            ...p,
            thumbnail: p.thumbnail ? getMediaUrl(p.thumbnail) : "",
            heroImage: p.heroImage ? getMediaUrl(p.heroImage) : "",
            detailImage: p.detailImage ? getMediaUrl(p.detailImage) : "",
            gallery: (p.gallery || []).map((g) => (g ? getMediaUrl(g) : "")),
          }));
          setProjects(staticProjects);
        }
      } catch (error) {
        console.error("Failed to fetch portfolio data:", error);
        // Fallback to static data on error
        setProjects(PortfolioModel.getAllProjects());
      }
    };

    fetchPortfolioData();

    // Initialize useScroll after mount
    if (sectionRef.current) {
      const { scrollYProgress: sectionScroll } = useScroll({
        target: sectionRef,
        offset: ["start start", "end start"],
      });
      setScrollYProgress(sectionScroll);
    }

    if (filterRef.current) {
      const { scrollYProgress: filterScroll } = useScroll({
        target: filterRef,
        offset: ["start end", "end start"],
      });
      setFilterProgress(filterScroll);
    }
  }, []);

  const handleHeroMouseMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const mx = e.clientX - (rect.left + rect.width / 2);
    const my = e.clientY - (rect.top + rect.height / 2);
    mouseX.set(mx / 30);
    mouseY.set(my / 30);
  };

  const categories = [
    "all",
    "digital-screens",
    "billboards",
    "mall-advertising",
    "digital-marketing",
  ];

  const categoryNames = {
    all: "جميع المشاريع",
    "digital-screens": "الشاشات الرقمية",
    billboards: "اللوحات الإعلانية",
    "mall-advertising": "إعلانات المولات",
    "digital-marketing": "التسويق الرقمي",
  };

  const filteredProjects =
    activeFilter === "all"
      ? projects
      : projects.filter((project) => project.category === activeFilter);

  if (!mounted) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-2xl font-bold text-[#0a367b]">جاري التحميل...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white" dir="rtl">
      <Navbar />

      {/* Hero Section */}
      <motion.section
        ref={heroRef}
        className="relative min-h-screen flex items-center justify-center overflow-hidden"
      >
        {/* Background Video */}
        <div className="absolute inset-0 will-change-transform">
          {heroData.video?.url ? (
            <video
              ref={videoRef}
              autoPlay
              muted
              loop
              playsInline
              preload="none"
              poster={getMediaUrl("hero.mp4")}
              className="w-full h-full object-cover"
            >
              <source src={heroData.video.url} type="video/mp4" />
            </video>
          ) : (
            <video
              autoPlay
              muted
              loop
              playsInline
              preload="none"
              className="w-full h-full object-cover"
            >
              <source src={getMediaUrl("hero.mp4")} type="video/mp4" />
            </video>
          )}
          <div className="absolute inset-0 bg-gradient-to-br from-[#0a367b]/90 via-[#0a367b]/70 to-[#0a367b]/50"></div>
        </div>

        {/* Floating Particles */}
        {mounted && (
          <div className="absolute inset-0">
            {floatingShapes.map((shape) => (
              <motion.div
                key={shape.id}
                className="absolute w-2 h-2 bg-[#dfa428]/40 rounded-full"
                style={{
                  top: `${shape.top}%`,
                  left: `${shape.left}%`,
                }}
                animate={{
                  y: [0, -100, 0],
                  opacity: [0, 1, 0],
                  scale: [0, 1.5, 0],
                }}
                transition={{
                  duration: shape.duration,
                  repeat: Number.POSITIVE_INFINITY,
                  delay: shape.delay,
                }}
              />
            ))}
          </div>
        )}

        {/* Content */}
        <div className="relative z-10 container mx-auto px-6 text-center text-white">
          <motion.div
            initial={{ opacity: 0, y: 100 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.5 }}
            className="max-w-5xl mx-auto"
          >
            <motion.h1
              className="text-5xl md:text-7xl font-bold mb-8 leading-tight"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1.2, delay: 1 }}
            >
              {heroData.title || "معرض أعمالنا"}
              {heroData.titleHighlight && (
                <>
                  <br />
                  <span className="text-[#dfa428]">{heroData.titleHighlight}</span>
                </>
              )}
            </motion.h1>

            <motion.p
              className="text-xl md:text-2xl leading-relaxed opacity-90 mb-12"
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 1.5 }}
            >
              {heroData.subtitle || "اكتشف مشاريعنا المتميزة في عالم الإعلان والتسويق الرقمي"}
            </motion.p>
          </motion.div>
        </div>

        {/* Geometric Background Elements */}
        <motion.div
          className="absolute top-20 right-20 w-32 h-32 bg-[#dfa428]/20 rounded-full"
          animate={{
            scale: [1, 1.3, 1],
            rotate: [0, 180, 360],
          }}
          transition={{ duration: 12, repeat: Number.POSITIVE_INFINITY }}
        />
        <motion.div
          className="absolute bottom-20 left-20 w-24 h-24 bg-white/10 rounded-full"
          animate={{
            scale: [1.2, 1, 1.2],
            rotate: [360, 180, 0],
          }}
          transition={{ duration: 8, repeat: Number.POSITIVE_INFINITY }}
        />
        <motion.div
          className="absolute top-1/3 left-1/4 w-16 h-16 bg-[#dfa428]/30 transform rotate-45"
          animate={{
            rotate: [45, 225, 405],
            scale: [1, 1.4, 1],
          }}
          transition={{ duration: 6, repeat: Number.POSITIVE_INFINITY }}
        />
      </motion.section>

      {/* Container Scroll Section */}
      <div
        ref={sectionRef}
        className="flex flex-col overflow-hidden"
        style={{
          background: "linear-gradient(0deg, #0C0927 0%, #233C78 100%)",
        }}
      ></div>

      {/* Filter Section */}
      <section
        ref={filterRef}
        className="py-20 bg-gray-50 relative overflow-hidden"
      >
        {/* Background Elements */}
        <div className="absolute inset-0">
          <motion.div
            className="absolute top-20 right-20 w-64 h-64 bg-gradient-to-br from-[#dfa428]/5 to-[#0a367b]/5 rounded-full"
            style={{ opacity: filterOpacity, scale: filterScale }}
            animate={{
              rotate: [0, 360],
              x: [0, 50, 0],
            }}
            transition={{ duration: 20, repeat: Infinity }}
          />
          <motion.div
            className="absolute bottom-20 left-20 w-48 h-48 bg-gradient-to-tr from-[#0a367b]/5 to-[#dfa428]/5 rounded-full"
            style={{ opacity: filterOpacity, scale: filterScale }}
            animate={{
              rotate: [360, 0],
              y: [0, -30, 0],
            }}
            transition={{ duration: 15, repeat: Infinity }}
          />
        </div>

        <div className="container mx-auto px-6 relative z-10">
          {/* Section Title */}
          <motion.div
            className="text-center mb-20"
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-[#0a367b] mb-6">
              مشاريعنا المتميزة
            </h2>
            <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              تصفح مجموعة من أبرز أعمالنا الإعلانية والتسويقية التي حققت نجاحاً
              استثنائياً لعملائنا في مختلف القطاعات
            </p>
          </motion.div>

          {/* Filter Buttons - All Categories Visible */}
          <motion.div
            className="mb-16"
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            viewport={{ once: true }}
          >
            <div className="flex flex-wrap items-center justify-center gap-3 sm:gap-4 px-4">
              {categories.map((category, index) => (
                <motion.button
                  key={category}
                  onClick={() => setActiveFilter(category)}
                  className={`cursor-pointer px-4 py-2 sm:px-6 sm:py-3 rounded-full font-semibold transition-all duration-300 whitespace-nowrap text-sm sm:text-base ${
                    activeFilter === category
                      ? "bg-[#0a367b] text-white shadow-lg"
                      : "yellow-button bg-white text-[#0a367b] hover:bg-[#dfa428] hover:text-[#0a367b] shadow-md"
                  }`}
                  whileHover={{ scale: 1.05, y: -2 }}
                  whileTap={{ scale: 0.95 }}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  {categoryNames[category]}
                </motion.button>
              ))}
            </div>
          </motion.div>

          {/* Projects Grid */}
          <div className="grid  md:grid-cols-2 lg:grid-cols-2  xl:grid-cols-3 g">
            {filteredProjects.map((project, index) => (
              <ProjectCard key={project.id} project={project} index={index} />
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

const ProjectCard = ({ project, index }) => {
  const cardRef = useRef(null);
  const [isCardMounted, setIsCardMounted] = useState(false);

  const isInView = useInView(cardRef, { once: true, margin: "-100px" });

  // Initialize scroll only after card is mounted
  const { scrollYProgress } = useScroll({
    target: cardRef,
    offset: ["start end", "end start"],
  });

  const cardY = useTransform(scrollYProgress, [0, 1], [50, -50]);
  const cardRotate = useTransform(scrollYProgress, [0, 1], [2, -2]);
  const cardScale = useTransform(scrollYProgress, [0, 1], [0.98, 1.02]);

  useEffect(() => {
    setIsCardMounted(true);
  }, []);

  return (
    <motion.div
      ref={cardRef}
      className="group"
      initial={{ opacity: 0, y: 100 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.8, delay: index * 0.1 }}
      style={
        isCardMounted ? { y: cardY, rotate: cardRotate, scale: cardScale } : {}
      }
    >
      <CardContainer className="inter-var py-0">
        <CardBody className="bg-white relative group/card hover:shadow-2xl hover:shadow-emerald-500/[0.1] border-gray-200 w-auto sm:w-[28rem] md:w-[20rem] xl:w-[25rem] h-auto rounded-xl sm:p-6 md:p-4 xl:p-6 border transition-all duration-500">
          <Link href={`/portfolio/${project.slug}`}>
            {/* Meta Row */}
            <CardItem
              as="div"
              translateZ="50"
              className="text-lg font-bold text-neutral-600 mb-2"
            >
              <div className="flex items-center justify-between mb-3">
                <span className="bg-[#dfa428] text-[#0a367b] px-2.5 py-0.5 rounded-full text-xs font-bold">
                  {project.categoryName}
                </span>
                <span className="text-xs text-gray-500 mr-2">
                  {project.year}
                </span>
              </div>
            </CardItem>

            {/* Title and Description */}
            <CardItem
              as="div"
              translateZ="60"
              className="text-neutral-500 text-sm max-w-sm mt-2 mb-3"
            >
              <h3 className="text-lg md:text-base xl:text-lg font-bold text-[#0a367b] mb-2 md:mb-1.5 xl:mb-2 leading-tight group-hover:text-yellow-600 transition-colors duration-300">
                {project.title}
              </h3>
              <p className="text-gray-600 leading-relaxed text-sm md:text-xs xl:text-sm">
                {project.description}
              </p>
            </CardItem>

            {/* Thumbnail */}
            <CardItem as="div" translateZ="100" className="w-full mt-3">
              <div className="relative h-52 md:h-40 xl:h-52 w-full rounded-xl overflow-hidden group-hover/card:shadow-xl transition-all duration-500">
                <Image
                  fill
                  src={
                    resolveMedia(project.thumbnail, 'portfolio-gallery.jpg') || '/fallback-image.png'
                  }
                  alt={project.title}
                  className="object-cover group-hover:scale-105 transition-transform duration-500"
                  onError={e => {
                    if (e.target && e.target.src !== window.location.origin + '/fallback-image.png') {
                      e.target.src = '/fallback-image.png';
                    }
                  }}
                />
              </div>
            </CardItem>

            {/* Footer Row */}
            <div className="flex justify-between items-center mt-4">
              <CardItem
                translateZ={20}
                as="div"
                className="px-3 py-1.5 rounded-xl text-xs font-normal"
              >
                <div className="flex items-center text-[#0a367b] font-semibold group-hover:text-yellow-600 transition-colors duration-300">
                  <span className="ml-2 text-xs">عرض التفاصيل</span>
                  <motion.span
                    animate={{ x: [0, 5, 0] }}
                    transition={{ duration: 1.5, repeat: Infinity }}
                  >
                    ←
                  </motion.span>
                </div>
              </CardItem>
              <CardItem
                translateZ={20}
                as="div"
                className="px-3 py-1.5 rounded-xl bg-[#0a367b] text-white text-xs font-bold hover:bg-[#0a367b]/90 transition-colors duration-300"
              >
                {project.client}
              </CardItem>
            </div>

            {/* Floating Particles */}
            <motion.div
              className="absolute top-4 left-4 w-2.5 h-2.5 bg-[#dfa428]/60 rounded-full"
              animate={{
                scale: [1, 1.5, 1],
                opacity: [0.6, 1, 0.6],
                rotate: [0, 180, 360],
              }}
              transition={{ duration: 3, repeat: Infinity, delay: index * 0.2 }}
            />
            <motion.div
              className="absolute bottom-4 right-4 w-2 h-2 bg-[#0a367b]/60 rounded-full"
              animate={{
                scale: [1.5, 1, 1.5],
                opacity: [0.4, 0.8, 0.4],
                y: [0, -10, 0],
              }}
              transition={{ duration: 4, repeat: Infinity, delay: index * 0.3 }}
            />
          </Link>
        </CardBody>
      </CardContainer>
    </motion.div>
  );
};
export default PortfolioPage;
